import { Button, Form, Modal, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../../i18n/i18n.constants";
import { useEffect, useMemo, useState } from "react";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { useFormik } from "formik";
import * as yup from "yup";
import { useUploadInvoiceMutation } from "../../../../app/api/invoicesApi";
import { useOrdersByUserIdQuery } from "../../../../app/api/ordersApi";
import { useCompaniesByUserIdQuery } from "../../../../app/api/companyApi";
import { InvoiceStatus } from "../../../../app/enums/invoiceStatus";
import { invoiceStatusMapper } from "../../../userProfile/invoices/invoiceStatusMappers";
import CustomSpinner from "../../../../components/spinner/CustomSpinner";
import CustomDatepicker from "../../../../components/customDatepicker/CustomDatepicker";
import { supportedCurrencies } from "../../../../app/constants/currencies";
import { getItemsFromObject } from "../../../../services/array/transformObject";
import "./UploadInvoiceModal.scss";

type Props = {
    onClose: () => void;
    isShow: boolean;
    orderNumber?: string;
};

const UploadInvoiceModal = ({ onClose, isShow, orderNumber: prefilledOrderNumber }: Props) => {
    const { t } = useTranslation(namespaces.modals);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const currencies = useMemo(() => getItemsFromObject(supportedCurrencies), []);

    const [uploadInvoiceHandler, { isLoading: isUploadLoading, isSuccess: isUploadSuccess }] =
        useUploadInvoiceMutation();

    // Only fetch orders if no prefilled order number
    const { data: orders } = useOrdersByUserIdQuery(undefined, {
        skip: !!prefilledOrderNumber,
    });
    const { data: companies } = useCompaniesByUserIdQuery();

    useEffect(() => {
        if (isUploadSuccess) {
            onClose();
        }
    }, [isUploadSuccess, onClose]);

    const formik = useFormik({
        initialValues: {
            orderNumber: prefilledOrderNumber ?? "",
            recipientCompanyId: "",
            type: "",
            invoiceNumber: "",
            seller: "",
            invoiceDate: null as Date | null,
            dueDate: null as Date | null,
            currency: supportedCurrencies.usd,
            amount: "",
            amountPaid: "",
            status: InvoiceStatus.Unpaid,
            description: "",
            comment: "",
        },
        validationSchema: yup.object().shape({
            orderNumber: yup.string().required(t("required.field", { ns: namespaces.validation })),
            recipientCompanyId: yup.string().required(t("required.field", { ns: namespaces.validation })),
            type: yup
                .string()
                .required(t("required.field", { ns: namespaces.validation }))
                .max(100, t("maxLength.field", { ns: namespaces.validation, count: 100 })),
            invoiceNumber: yup
                .string()
                .required(t("required.field", { ns: namespaces.validation }))
                .max(100, t("maxLength.field", { ns: namespaces.validation, count: 100 })),
            seller: yup
                .string()
                .required(t("required.field", { ns: namespaces.validation }))
                .max(255, t("maxLength.field", { ns: namespaces.validation, count: 255 })),
            invoiceDate: yup
                .date()
                .nullable()
                .required(t("required.field", { ns: namespaces.validation })),
            dueDate: yup
                .date()
                .nullable()
                .required(t("required.field", { ns: namespaces.validation }))
                .min(yup.ref("invoiceDate"), t("invoices.upload.dueDateAfterInvoiceDate")),
            currency: yup
                .string()
                .required(t("required.field", { ns: namespaces.validation }))
                .max(10, t("maxLength.field", { ns: namespaces.validation, count: 10 })),
            amount: yup
                .number()
                .required(t("required.field", { ns: namespaces.validation }))
                .positive(t("positive.field", { ns: namespaces.validation }))
                .typeError(t("number.field", { ns: namespaces.validation })),
            amountPaid: yup
                .number()
                .required(t("required.field", { ns: namespaces.validation }))
                .min(0, t("min.field", { ns: namespaces.validation, min: 0 }))
                .max(yup.ref("amount"), t("invoices.upload.amountPaidExceedsAmount"))
                .typeError(t("number.field", { ns: namespaces.validation })),
            status: yup
                .mixed<InvoiceStatus>()
                .oneOf(Object.values(InvoiceStatus) as InvoiceStatus[])
                .required(t("required.field", { ns: namespaces.validation })),
            description: yup.string().max(500, t("maxLength.field", { ns: namespaces.validation, count: 500 })),
            comment: yup.string().max(500, t("maxLength.field", { ns: namespaces.validation, count: 500 })),
        }),
        onSubmit: async (values) => {
            if (!selectedFile) {
                return;
            }

            await uploadInvoiceHandler({
                file: selectedFile,
                ...values,
                amount: Number(values.amount),
                amountPaid: Number(values.amountPaid),
                invoiceDate: values.invoiceDate ? values.invoiceDate.toISOString() : "",
                dueDate: values.dueDate ? values.dueDate.toISOString() : "",
            });
        },
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                alert(t("invoices.upload.fileSizeError"));
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (value === "") {
            formik.setFieldValue(name, "");
            return;
        }

        if (/^\d*\.?\d{0,2}$/.test(value)) {
            const sanitizedValue = value.replace(/^0+(?=\d)/, "");
            formik.setFieldValue(name, sanitizedValue);
        }
    };

    const handleInvoiceDateChange = (date: Date | null) => {
        formik.setFieldValue("invoiceDate", date);
        // Reset dueDate if it's before the new invoiceDate
        if (date && formik.values.dueDate && formik.values.dueDate < date) {
            formik.setFieldValue("dueDate", null);
        }
    };

    const handleDueDateChange = (date: Date | null) => {
        formik.setFieldValue("dueDate", date);
    };

    return (
        <Modal
            show={isShow}
            size="xl"
            onHide={onClose}
            backdrop="static"
            centered
            fullscreen="md-down"
            className="upload-invoice-modal"
            contentClassName="upload-invoice-modal-content"
            aria-labelledby="upload-invoice-modal"
        >
            <Modal.Header closeButton>
                <div className="d-flex align-items-center flex-grow-1">
                    <div className="pe-3">
                        <FaFileInvoiceDollar size={24} />
                    </div>
                    <span className="fs-4 fw-bold">{t("invoices.upload.title")}</span>
                </div>
            </Modal.Header>
            <Modal.Body className="py-4 px-5">
                {isUploadLoading ? (
                    <CustomSpinner hideTextTitle={true} />
                ) : (
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.file")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        className="custom-form-control"
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                                        isInvalid={!selectedFile && formik.submitCount > 0}
                                    />
                                    {selectedFile && (
                                        <Form.Text className="text-muted">
                                            {t("invoices.upload.selectedFile")}: {selectedFile.name}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            {!prefilledOrderNumber && (
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t("invoices.upload.orderNumber")} <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="orderNumber"
                                            className="custom-form-control"
                                            value={formik.values.orderNumber}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            isInvalid={!!formik.errors.orderNumber && !!formik.touched.orderNumber}
                                        >
                                            <option value="">{t("invoices.upload.selectOrder")}</option>
                                            {orders?.map((order) => (
                                                <option key={order.orderNumber} value={order.orderNumber}>
                                                    {order.orderNumber}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.orderNumber}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            )}
                            <Col md={prefilledOrderNumber ? 6 : 4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.invoiceNumber")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="invoiceNumber"
                                        className="custom-form-control"
                                        value={formik.values.invoiceNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.invoiceNumber && !!formik.touched.invoiceNumber}
                                        placeholder={t("invoices.upload.invoiceNumberPlaceholder")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.invoiceNumber}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={prefilledOrderNumber ? 6 : 4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.recipientCompany")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="recipientCompanyId"
                                        className="custom-form-control"
                                        value={formik.values.recipientCompanyId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={
                                            !!formik.errors.recipientCompanyId && !!formik.touched.recipientCompanyId
                                        }
                                    >
                                        <option value="">{t("invoices.upload.selectCompany")}</option>
                                        {companies?.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.companyName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.recipientCompanyId}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.type")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="type"
                                        className="custom-form-control"
                                        value={formik.values.type}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.type && !!formik.touched.type}
                                        placeholder={t("invoices.upload.typePlaceholder")}
                                    />
                                    <Form.Control.Feedback type="invalid">{formik.errors.type}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.seller")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="seller"
                                        className="custom-form-control"
                                        value={formik.values.seller}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.seller && !!formik.touched.seller}
                                        placeholder={t("invoices.upload.sellerPlaceholder")}
                                    />
                                    <Form.Control.Feedback type="invalid">{formik.errors.seller}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.invoiceDate")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <CustomDatepicker
                                        id="invoiceDate"
                                        nameElement="invoiceDate"
                                        value={formik.values.invoiceDate ?? undefined}
                                        onChange={handleInvoiceDateChange}
                                        onBlur={() => formik.setFieldTouched("invoiceDate", true)}
                                        isInvalid={!!formik.errors.invoiceDate && !!formik.touched.invoiceDate}
                                        isValid={!formik.errors.invoiceDate && !!formik.touched.invoiceDate}
                                    />
                                    {formik.errors.invoiceDate && formik.touched.invoiceDate && (
                                        <div className="invalid-feedback" style={{ display: "block" }}>
                                            {formik.errors.invoiceDate as string}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.dueDate")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <CustomDatepicker
                                        id="dueDate"
                                        nameElement="dueDate"
                                        value={formik.values.dueDate ?? undefined}
                                        onChange={handleDueDateChange}
                                        onBlur={() => formik.setFieldTouched("dueDate", true)}
                                        minDate={formik.values.invoiceDate ?? undefined}
                                        isInvalid={!!formik.errors.dueDate && !!formik.touched.dueDate}
                                        isValid={!formik.errors.dueDate && !!formik.touched.dueDate}
                                    />
                                    {formik.errors.dueDate && formik.touched.dueDate && (
                                        <div className="invalid-feedback" style={{ display: "block" }}>
                                            {formik.errors.dueDate as string}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.currency")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="currency"
                                        className="custom-form-control"
                                        value={formik.values.currency}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.currency && !!formik.touched.currency}
                                    >
                                        {currencies.map((curr) => (
                                            <option key={curr} value={curr}>
                                                {curr}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.currency}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.amount")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="amount"
                                        className="custom-form-control"
                                        value={formik.values.amount}
                                        onChange={handleAmountChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.amount && !!formik.touched.amount}
                                        placeholder="0.00"
                                        autoComplete="off"
                                    />
                                    <Form.Control.Feedback type="invalid">{formik.errors.amount}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.amountPaid")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="amountPaid"
                                        className="custom-form-control"
                                        value={formik.values.amountPaid}
                                        onChange={handleAmountChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.amountPaid && !!formik.touched.amountPaid}
                                        placeholder="0.00"
                                        autoComplete="off"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.amountPaid}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("invoices.upload.status")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        className="custom-form-control"
                                        value={formik.values.status}
                                        onChange={(e) => formik.setFieldValue("status", Number(e.target.value))}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.status && !!formik.touched.status}
                                    >
                                        {Object.values(InvoiceStatus)
                                            .filter((v) => typeof v === "number")
                                            .map((status) => (
                                                <option key={status} value={status}>
                                                    {invoiceStatusMapper(status as InvoiceStatus)}
                                                </option>
                                            ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{formik.errors.status}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("invoices.upload.description")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="description"
                                        className="custom-form-control"
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.description && !!formik.touched.description}
                                        placeholder={t("invoices.upload.descriptionPlaceholder")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("invoices.upload.comment")}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="comment"
                                        className="custom-form-control"
                                        value={formik.values.comment}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={!!formik.errors.comment && !!formik.touched.comment}
                                        placeholder={t("invoices.upload.commentPlaceholder")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.comment}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Button
                                    variant="primary"
                                    disabled={!formik.isValid || !selectedFile || isUploadSuccess}
                                    type="submit"
                                    className="w-100 my-3 p-3"
                                >
                                    {t("buttons.upload", { ns: namespaces.common })}
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    variant="secondary"
                                    disabled={isUploadSuccess}
                                    className="w-100 my-3 p-3"
                                    onClick={onClose}
                                >
                                    {t("buttons.cancel", { ns: namespaces.common })}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default UploadInvoiceModal;
